import { IProactiveScenario } from '@/constants/crispProactive';

/**
 * TR-493 — low-level helpers cho proactive Crisp messages (spec.md §6, D3–D7, D12).
 *
 * Mọi truy cập Crisp đi qua global `window.$crisp` (đồng bộ `src/helpers/crisp.ts`) và
 * fail-closed: chưa load / lỗi → KHÔNG throw, KHÔNG show.
 */

/** Subset Crisp global mà lớp proactive dùng (crisp-sdk-web khai báo `var $crisp: any`). */
export interface ICrispGlobal {
  push: (command: unknown[]) => void;
  is: (key: string) => boolean;
  get: (key: string) => unknown;
}

declare global {
  interface Window {
    /** Native Crisp gọi khi chatbox sẵn sàng (chỉ lúc này `.is()`/`.get()` mới an toàn). */
    CRISP_READY_TRIGGER?: () => void;
  }
}

const SESSION_KEY_PREFIX = 'crisp_proactive_';
/** Cờ tổng: đã có proactive nào fire trong session chưa (gate scenario fallback #13). */
export const SESSION_KEY_ANY = 'crisp_proactive_any';

export const sessionKeyFor = (id: string): string => `${SESSION_KEY_PREFIX}${id}`;

/** Lấy `$crisp` global đã typed; `undefined` khi chưa load / ngoài browser. */
export const getCrisp = (): ICrispGlobal | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { $crisp?: ICrispGlobal }).$crisp;
};

export const isCrispReady = (): boolean => !!getCrisp();

export const hasFiredInSession = (id: string): boolean => {
  try {
    return window.sessionStorage.getItem(sessionKeyFor(id)) === '1';
  } catch {
    return false;
  }
};

export const hasFiredAnyInSession = (): boolean => {
  try {
    return window.sessionStorage.getItem(SESSION_KEY_ANY) === '1';
  } catch {
    return false;
  }
};

export const markFiredInSession = (id: string): void => {
  try {
    window.sessionStorage.setItem(sessionKeyFor(id), '1');
    window.sessionStorage.setItem(SESSION_KEY_ANY, '1');
  } catch {
    // sessionStorage không khả dụng → bỏ qua (proactive vẫn fail-safe).
  }
};

/**
 * Khớp pathname với scenario.
 * `exact` → `pathname === path`. `prefix` → đúng path hoặc sub-path `path/...`
 * (KHÔNG dùng `startsWith(path)` trần để `/plan` không dính `/plan-x`).
 */
export const matchScenarioPath = (pathname: string, scenario: IProactiveScenario): boolean => {
  if (scenario.match === 'exact') return pathname === scenario.path;
  return pathname === scenario.path || pathname.startsWith(`${scenario.path}/`);
};

/** Chọn ngẫu nhiên 1 message variant (P0-5). */
export const pickRandomMessage = (messages: string[]): string => {
  if (!messages.length) return '';
  return messages[Math.floor(Math.random() * messages.length)];
};

/**
 * Khách đã có conversation đang diễn ra (đã gửi/nhận message thật) chưa.
 * Fail-closed: chưa ready / lỗi → `false` (chưa reply) — việc chặn show vẫn do
 * {@link isWebsiteAvailable} đảm nhiệm (cũng fail-closed).
 */
export const isSessionOngoing = (): boolean => {
  const crisp = getCrisp();
  if (!crisp) return false;
  try {
    return crisp.is('session:ongoing') === true;
  } catch {
    return false;
  }
};

/**
 * Support có online không (P0-6). Fail-closed: chưa ready / lỗi → `false` (KHÔNG mời chat
 * khi không chắc có người trả lời).
 */
export const isWebsiteAvailable = (): boolean => {
  const crisp = getCrisp();
  if (!crisp) return false;
  try {
    return crisp.is('website:available') === true;
  } catch {
    return false;
  }
};

/**
 * Hiện 1 proactive message local-only (P0-1) + đẩy impression event (D7) + mark fired (P0-3).
 * Guard `window.$crisp`: chưa load → skip im lặng, KHÔNG throw.
 */
export const showProactiveMessage = (id: string, message: string): void => {
  const crisp = getCrisp();
  if (!crisp || !message) return;
  try {
    crisp.push(['do', 'message:show', ['text', message]]);
    crisp.push(['set', 'session:event', [[['proactive_shown', { scenario: id }, 'blue']]]]);
    markFiredInSession(id);
  } catch {
    // Không bao giờ để lỗi Crisp nổi lên UI.
  }
};
